import { useEffect, useState, useMemo, useCallback } from "react";
import { useAuth } from "../contexts/auth.context";
import { SubscriptionService } from "./subscription.service";
import type { UsageStats, LimitCheckResult } from "./subscription.types";
import type { PlanLimits } from "../auth/auth.types";

/**
 * Hook for managing subscription limits and usage across the application
 *
 * This hook provides:
 * - Current usage statistics (chats, storage episodes, workspaces)
 * - Limit checking for various features
 * - Feature access checks
 * - Helper methods for displaying limits in UI
 *
 * @example
 * ```tsx
 * const { limits, usage, chatLimit, canCreateChat, refreshUsage } = useSubscription();
 *
 * if (!chatLimit.allowed) {
 *   return <UpgradePrompt message="You've reached your chat limit" />;
 * }
 * ```
 */
export function useSubscription() {
	const { user, isAuthenticated } = useAuth();
	const [usage, setUsage] = useState<UsageStats>({
		chats: 0,
		storageEpisodes: 0,
		workspaces: 0,
	});
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Get limits from auth context (already fetched from /session/me)
	const limits = user?.limits;

	/**
	 * Fetch current usage statistics from the backend
	 */
	const refreshUsage = useCallback(async () => {
		if (!isAuthenticated || !limits) {
			return;
		}

		setIsLoading(true);
		setError(null);

		try {
			const stats = await SubscriptionService.getUserUsage();
			setUsage(stats);
		} catch (err) {
			console.error("Failed to refresh usage:", err);
			setError(err instanceof Error ? err.message : "Failed to refresh usage");
		} finally {
			setIsLoading(false);
		}
	}, [isAuthenticated, limits]);

	// Auto-fetch usage when user logs in
	useEffect(() => {
		if (isAuthenticated && limits) {
			refreshUsage();
		}
	}, [isAuthenticated, limits, refreshUsage]);

	// Calculate limit checks
	const chatLimit = useMemo<LimitCheckResult>(() => {
		if (!limits) {
			return {
				allowed: true,
				remaining: "unlimited",
				isAtLimit: false,
				isNearLimit: false,
				maxAllowed: "unlimited",
				current: 0,
			};
		}
		return SubscriptionService.canCreateChat(usage.chats, limits);
	}, [usage.chats, limits]);

	const storageLimit = useMemo<LimitCheckResult>(() => {
		if (!limits) {
			return {
				allowed: true,
				remaining: "unlimited",
				isAtLimit: false,
				isNearLimit: false,
				maxAllowed: "unlimited",
				current: 0,
			};
		}
		return SubscriptionService.canStoreEpisode(usage.storageEpisodes, limits);
	}, [usage.storageEpisodes, limits]);

	// Feature checks
	const hasFeature = useCallback(
		(featureKey: keyof PlanLimits["features"]): boolean => {
			if (!limits) return false;
			return SubscriptionService.hasFeature(featureKey, limits);
		},
		[limits],
	);

	// Helper methods
	const canCreateChat = useMemo(() => chatLimit.allowed, [chatLimit.allowed]);

	const canStoreEpisode = useMemo(
		() => storageLimit.allowed,
		[storageLimit.allowed],
	);

	const formatLimit = useCallback((value: number): string => {
		return SubscriptionService.formatLimit(value);
	}, []);

	const getUpgradeUrl = useCallback((): string => {
		return SubscriptionService.getUpgradeUrl();
	}, []);

	/**
	 * Increment usage count locally (optimistic update)
	 * Call this after successfully creating a chat/episode to avoid re-fetch
	 */
	const incrementUsage = useCallback((key: keyof UsageStats) => {
		setUsage((prev) => ({
			...prev,
			[key]: prev[key] + 1,
		}));
	}, []);

	/**
	 * Decrement usage count locally (optimistic update)
	 * Call this after successfully deleting a episode
	 */
	const decrementUsage = useCallback((key: keyof UsageStats) => {
		setUsage((prev) => ({
			...prev,
			[key]: Math.max(0, prev[key] - 1),
		}));
	}, []);

	return {
		// State
		limits,
		usage,
		isLoading,
		error,

		// Limit checks
		chatLimit,
		storageLimit,

		// Simple boolean checks
		canCreateChat,
		canStoreEpisode,

		// Feature checks
		hasFeature,

		// Methods
		refreshUsage,
		incrementUsage,
		decrementUsage,
		formatLimit,
		getUpgradeUrl,

		// User info
		subscriptionStatus: user?.subscriptionStatus,
		currentPlanId: user?.currentPlanId,
	};
}
